// src/pages/use-cases/MediaPage.js
// Targets: media localization, video content localization, broadcaster dubbing
import UseCasePage from './UseCasePage';

const DATA = {
  slug: 'media',
  breadcrumbLabel: 'Media Companies',
  seo: {
    title: 'AI Video Dubbing for Media Companies & Studios | DubLabs',
    description: 'Scale video localization across your library with DubLabs AI dubbing. 32 languages, frame-accurate lip-sync, weeks compressed to minutes. Book a demo.',
  },
  eyebrow: 'For Media & Broadcast',
  headline: 'Localize your library at broadcast scale.',
  subhead: 'DubLabs powers video localization for media companies, studios, and streaming platforms — 32 languages processed in parallel, weeks of studio work collapsed into minutes.',
  problem: 'A single feature-length localization run means 4–8 weeks per language, six figures of talent + studio cost, and a different voice actor in every market. Multiply that across a catalog and localization becomes the bottleneck to global distribution.',
  features: [
    {
      number: '01',
      title: 'Original speaker in every language',
      body: 'Instead of recasting each language with a different voice actor, DubLabs clones the original speaker\'s voice. Continuity across markets that studios can\'t deliver at any price.',
    },
    {
      number: '02',
      title: 'All languages, in parallel',
      body: 'Studios dub one language at a time — that\'s how the calendar works. DubLabs processes every target language simultaneously. A 30-minute episode into 8 languages in under 20 minutes.',
    },
    {
      number: '03',
      title: 'Iterate without re-booking studios',
      body: 'Script change halfway through a season? Re-dub the affected episodes in minutes, same voice, same tone. No booking, no travel, no re-recording sessions.',
    },
    {
      number: '04',
      title: 'Frame-accurate lip-sync at broadcast scale',
      body: 'Our lip-sync AI re-syncs mouth movements to the dubbed audio on your existing footage — frame by frame. No jarring lip-audio mismatch, no reshoots. Works across episodic, feature-length, and daily broadcast formats.',
    },
    {
      number: '05',
      title: 'Enterprise-grade infrastructure',
      body: 'Custom SLA, dedicated support, API access, and white-label options. Built on AWS with SOC 2-ready security. Fits into existing broadcast + streaming pipelines.',
    },
  ],
  scenarios: [
    {
      label: 'Scenario 01',
      headline: 'Streaming platform localizes a 40-episode series into 6 languages in 5 days.',
      body: 'Traditional studio quote: 6 months, ~$1.2M. DubLabs runs all episodes in parallel across all 6 languages. Same actors\' voices preserved across every episode, every language. Ships to global subscribers before the marketing window closes.',
    },
    {
      label: 'Scenario 02',
      headline: 'News broadcaster distributes daily bulletins to 12 language markets.',
      body: 'Daily 15-minute bulletin needs to hit 12 markets by prime time. Studios can\'t match that turnaround. DubLabs dubs the daily bulletin into all 12 languages within an hour of ingest — same anchor voices, same tone, ready for scheduled broadcast.',
    },
    {
      label: 'Scenario 03',
      headline: 'Educational broadcaster localizes back-catalog for regional syndication.',
      body: '200 hours of documentary content in English, licensed to regional broadcasters in India, LATAM, and MENA. DubLabs processes the entire catalog across 4 languages in a week. Fraction of the cost of studio dubbing, and every regional broadcaster gets identical voice continuity to the English original.',
    },
  ],
  faqs: [
    {
      q: 'How does DubLabs compare to Deepdub and other enterprise dubbing platforms?',
      a: 'Deepdub is optimized for prestige M&E — feature-length film with human adapters in the loop. DubLabs is purpose-built for broadcast-scale video: series, news, documentaries, corporate. Hands-on partnership with our team, sub-4-minute short-clip turnaround, per-video pricing instead of six-figure annual contracts. See our /vs/deepdub page for the detailed comparison.',
    },
    {
      q: 'Can we integrate DubLabs into our existing content pipeline?',
      a: 'Yes. We offer API access for programmatic dubbing, webhook callbacks for job completion, and support for common broadcast delivery formats. Contact us and we\'ll scope the integration to your existing tooling.',
    },
    {
      q: 'What about SLA, security, and compliance for enterprise deployments?',
      a: 'Enterprise plans include a custom SLA with priority processing, dedicated account manager, SOC 2-ready infrastructure on AWS, GDPR-compliant data handling, and end-to-end encryption. Content is deleted from our systems on your schedule.',
    },
    {
      q: 'Do we own the dubbed output? What about voice rights?',
      a: 'You own all dubbed output. Voice clones are created only for content where you have the rights to the original speaker\'s voice — we don\'t use voice cloning to impersonate individuals without consent. Enterprise contracts spell out voice-use rights explicitly.',
    },
    {
      q: 'How does lip-sync hold up on episodic content at scale?',
      a: 'Our lip-sync model is trained specifically for the multi-take, multi-speaker footage typical of episodic broadcast. Same accuracy across a 22-episode season as on a single trailer. For feature-length prestige work with human-adapter polish, studios remain the ceiling — but for series, news, and documentary, DubLabs\' lip-sync is production-grade.',
    },
    {
      q: 'How do you handle sensitive or exclusive content pre-release?',
      a: 'Enterprise content is processed in isolated infrastructure with restricted access, no data retention beyond the job window, and audit logs available on request. Suitable for pre-release, embargoed, and confidential content.',
    },
  ],
};

export default function MediaPage() {
  return <UseCasePage {...DATA} />;
}
